// NFC Configuration Manager - Dynamic Configuration Loading
class NFCConfigManager {
  constructor() {
    this.config = null;
    this.language = 'en';
    this.loadPromise = null;
  }

  // Load configuration from external JSON
  async loadConfig() {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this._fetchConfig();
    return this.loadPromise;
  }

  async _fetchConfig() {
    try {
      console.log('🔧 Loading NFC configuration...');

      const response = await fetch('./config/nfc-config.json');
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status}`);
      }

      const config = await response.json();
      this.config = config;

      // Detect user language
      const userLang = navigator.language.substring(0, 2);
      if (config.texts[userLang]) {
        this.language = userLang;
      }

      console.log('✅ NFC configuration loaded:', {
        version: config.version,
        language: this.language
      });

      return config;

    } catch (error) {
      console.error('❌ Failed to load NFC configuration:', error);

      // Fallback configuration
      this.config = this._getFallbackConfig();
      return this.config;
    }
  }

  // Get fallback configuration if external config fails
  _getFallbackConfig() {
    return {
      version: '1.0.0-fallback',
      scanTimeout: 30000,
      maxRetries: 3,
      cooldownDays: 3,
      temporalDays: 7,
      texts: {
        en: {
          scanInstructions: 'Hold your NFC device near the sensor and wait for confirmation',
          scanning: 'Scanning for NFC device...',
          detected: 'NFC device detected!',
          validating: 'Validating NFC credentials...',
          success: 'NFC authentication successful!',
          timeout: 'Scan timeout. Please try again.',
          notSupported: 'NFC is not supported on this device',
          invalidChip: 'Invalid NFC chip - not a valid LOS DIAS merchandise',
          shareTitle: '🎫 Unlock WAYFARE Premium!',
          shareText: 'I\'m using WAYFARE Premium! Get the app and scan my NFC chip for 7-day trial access.',
          shareSubject: 'Try WAYFARE Premium - 7 days free!'
        }
      },
      deepLink: {
        baseUrl: 'https://travel.dias.com',
        fallbackUrl: 'https://travel.dias.com/download',
        appScheme: 'diastravel://nfc'
      },
      rateLimiting: {
        maxAttemptsPerMinute: 10,
        maxAttemptsPerHour: 50,
        blockDurationMinutes: 15
      },
      security: {
        enableEncryption: true,
        logNFCAccess: true,
        requireDeviceVerification: false
      }
    };
  }

  // Get current configuration
  async getConfig() {
    if (!this.config) {
      await this.loadConfig();
    }
    return this.config;
  }

  // Get valid NFC keys - DEPRECATED (validation moved to server)
  async getValidNFCKeys() {
    console.warn('⚠️ getValidNFCKeys() is deprecated. NFC validation is now done server-side.');
    return [];
  }

  // Get localized text
  async getText(key, params = {}) {
    const config = await this.getConfig();
    let text = config.texts[this.language]?.[key] || config.texts.en?.[key] || key;

    // Replace parameters
    Object.keys(params).forEach(param => {
      text = text.replace(`{${param}}`, params[param]);
    });

    return text;
  }

  // Get scan timeout
  async getScanTimeout() {
    const config = await this.getConfig();
    return config.scanTimeout;
  }

  // Get deep link configuration
  async getDeepLinkConfig() {
    const config = await this.getConfig();
    return config.deepLink;
  }

  // Get rate limiting configuration
  async getRateLimitConfig() {
    const config = await this.getConfig();
    return config.rateLimiting;
  }

  // Get security configuration
  async getSecurityConfig() {
    const config = await this.getConfig();
    return config.security;
  }

  // Check if NFC key is valid - DEPRECATED (validation moved to server)
  async isValidNFCKey(nfcKey) {
    console.warn('⚠️ isValidNFCKey() is deprecated. NFC validation is now done server-side.');
    // Always return true - let server validate
    return true;
  }

  // Get temporal days setting
  async getTemporalDays() {
    const config = await this.getConfig();
    return config.temporalDays;
  }

  // Get cooldown days setting
  async getCooldownDays() {
    const config = await this.getConfig();
    return config.cooldownDays;
  }

  // Set language
  setLanguage(lang) {
    this.language = lang;
  }

  // Get share URL with deep linking
  async getShareUrl(nfcId = null) {
    const config = await this.getConfig();
    const baseUrl = config.deepLink.baseUrl;

    if (nfcId) {
      return `${baseUrl}?nfc=${encodeURIComponent(nfcId)}&source=share`;
    }

    return baseUrl;
  }

  // Get share data for native sharing
  async getShareData(nfcId = null) {
    const shareUrl = await this.getShareUrl(nfcId);
    const title = await this.getText('shareTitle');
    const text = await this.getText('shareText', { url: shareUrl });

    return {
      title,
      text,
      url: shareUrl
    };
  }

  // Validate configuration
  validateConfig(config) {
    const required = ['scanTimeout', 'texts', 'deepLink'];

    for (const field of required) {
      if (!config[field]) {
        throw new Error(`Missing required configuration field: ${field}`);
      }
    }

    return true;
  }

  // Reload configuration (for admin)
  async reloadConfig() {
    this.config = null;
    this.loadPromise = null;
    return this.loadConfig();
  }
}

export default new NFCConfigManager();